// askSmart-ui/src/pages/TOSP/tabs/ConfigurationTab.tsx
import React, { useMemo } from "react";
import ConfigurationPanel from "../components/ConfigurationPanel";
import BmvaFeesPanel from "../components/BmvaFeesPanel";
import CamCategoryPanel from "../components/CamCategoryPanel";
import WastePsfCalculatorPanel from "../components/WastePsfCalculatorPanel";
import { EntityOption } from "../components/SplitMappingFields";
import { useTospData } from "../TospDataContext";
import {
  CamCategory,
  COMMERCIAL_SPACE_TYPE,
  isOccupancyActive,
  SplitAmong,
  Tenant,
  tenantLabel,
  TospConfiguration,
} from "../types";
import { sameText } from "../validation";

// Configuration — the lookup lists the rest of TOSP reads its dropdowns from,
// so a new business category is an admin change rather than a code change.
// Each panel commits independently, as the PayFlow payroll rules do.
export default function ConfigurationTab() {
  const { configuration, saveConfiguration, buildings, units, tenants, unitLabel } =
    useTospData();

  const commit = (key: keyof TospConfiguration) => (values: string[]) =>
    saveConfiguration({ ...configuration, [key]: values });

  /**
   * The units any split can actually divide a cost across.
   *
   * Every CAM split is charged against COMMERCIAL area only — storage,
   * basement, mezzanine and enclosed patio never take a share. That rule is
   * enforced where it counts, in allocateCategory, which filters each entity's
   * units before weighing them; here it decides what the picker is allowed to
   * offer, so the two can't disagree.
   */
  const commercialUnitIds = useMemo(
    () =>
      new Set(
        units
          .filter((unit) => sameText(unit.spaceType, COMMERCIAL_SPACE_TYPE))
          .map((unit) => unit.id),
      ),
    [units],
  );

  /**
   * Does this tenant hold commercial space today?
   *
   * A tenant who holds only storage brings no area into a split, so choosing
   * them adds nothing and their share lands on everyone else instead — an
   * error with no symptom. Live leases only: a lease that has ended demises
   * nothing to divide.
   */
  const holdsCommercialSpace = (tenant: Tenant): boolean =>
    (tenant.occupancies || [])
      .filter(isOccupancyActive)
      .some((occupancy) =>
        (occupancy.leasedSpaces || []).some((space) => commercialUnitIds.has(space.unitId)),
      );

  // What a split's Entity dropdown offers, given what it splits among. The
  // first three are real records referenced by id; Waste Removal is the
  // committed list beside it, so the value is the text itself.
  const entityOptions = (splitAmong: SplitAmong | ""): EntityOption[] => {
    switch (splitAmong) {
      case "Buildings":
        return buildings.map((b) => ({ value: b.id, label: b.name }));
      case "Units":
        return units.map((u) => ({
          value: u.id,
          label: unitLabel(u),
        }));
      case "Tenants":
        return tenants
          .filter(holdsCommercialSpace)
          .map((t) => ({ value: t.id, label: tenantLabel(t) }));
      case "Waste Removal":
        return configuration.wasteRemovalTypes.map((value) => ({ value, label: value }));
      default:
        return [];
    }
  };

  return (
    <section className="tosp-card">
      {/* Three columns: the tenant-facing lists, the waste lists, then CAM
          Categories — the two short lists in each of the first two columns
          stack rather than each taking a column of their own. */}
      <div className="tosp-config-grid">
        <div className="tosp-config-column">
          <ConfigurationPanel
            title="Retailer Types"
            description="Create and edit business categories for tenant classification dropdown"
            usageLabel="the Tenant form"
            values={configuration.retailerTypes}
            usageCount={(value) => tenants.filter((t) => sameText(t.retailerType, value)).length}
            onCommit={commit("retailerTypes")}
          />

          <ConfigurationPanel
            title="Space Types"
            description="Create and Edit Space types for Tenant assignment"
            usageLabel="the Unit form"
            values={configuration.spaceTypes}
            usageCount={(value) => units.filter((u) => sameText(u.spaceType, value)).length}
            onCommit={commit("spaceTypes")}
          />

          {/* Under Space Types because it's the other thing said once about
              every unit. Staged behind a Commit like the lists around it —
              these two figures reprice the whole portfolio when they land. */}
          <BmvaFeesPanel
            ratePerSqFt={configuration.bmvaRatePerSqFt}
            royaltyPercentage={configuration.bmvaRoyaltyPercentage}
            exemptSpaceTypes={configuration.bmvaExemptSpaceTypes}
            exemptUnitIds={configuration.bmvaExemptUnitIds}
            spaceTypes={configuration.spaceTypes}
            // The same naming the CAM splits use, so a unit reads the same
            // wherever it's picked.
            unitOptions={units.map((unit) => ({
              id: unit.id,
              label: unitLabel(unit),
            }))}
            onCommit={({
              ratePerSqFt,
              royaltyPercentage,
              exemptSpaceTypes,
              exemptUnitIds,
            }) =>
              saveConfiguration({
                ...configuration,
                bmvaRatePerSqFt: ratePerSqFt,
                bmvaRoyaltyPercentage: royaltyPercentage,
                bmvaExemptSpaceTypes: exemptSpaceTypes,
                bmvaExemptUnitIds: exemptUnitIds,
              })
            }
            units={units}
            tenants={tenants}
          />
        </div>

        <div className="tosp-config-column">
          <ConfigurationPanel
            title="Waste Removal Types"
            description="Create and edit garbage removal categories for tenant classification dropdown"
            usageLabel="the Tenant form"
            values={configuration.wasteRemovalTypes}
            usageCount={(value) => tenants.filter((t) => sameText(t.wasteRemoval, value)).length}
            onCommit={commit("wasteRemovalTypes")}
          />

          {/* Calculator inputs save as they're entered — unlike the lists
              beside it, nothing else reads them, so there's nothing to stage
              behind a Commit. */}
          <WastePsfCalculatorPanel
            wasteTypes={configuration.wasteRemovalTypes}
            units={units}
            tenants={tenants}
            totalCost={configuration.wasteTotalCost}
            onTotalCostChange={(wasteTotalCost) =>
              saveConfiguration({ ...configuration, wasteTotalCost })
            }
            percentages={configuration.wasteSharePercentages || {}}
            onPercentagesChange={(wasteSharePercentages) =>
              saveConfiguration({ ...configuration, wasteSharePercentages })
            }
          />
        </div>

        <CamCategoryPanel
          values={configuration.camCategories}
          entityOptions={entityOptions}
          onCommit={(camCategories: CamCategory[]) =>
            saveConfiguration({ ...configuration, camCategories })
          }
        />
      </div>
    </section>
  );
}
